import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, GripVertical } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyQuestion = () => ({
  id: `temp-${Date.now()}-${Math.random()}`,
  text: "",
  options: [
    { id: `opt-${Date.now()}-1`, text: "", is_correct: false },
    { id: `opt-${Date.now()}-2`, text: "", is_correct: false },
    { id: `opt-${Date.now()}-3`, text: "", is_correct: false },
    { id: `opt-${Date.now()}-4`, text: "", is_correct: false },
  ],
  explanation: "",
});

export const AdminExamEdit = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const isNew = !examId;
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    time_limit_minutes: "",
    questions_per_attempt: "",
    questions: [emptyQuestion()],
  });

  useEffect(() => {
    if (!isNew) {
      fetchExam();
    }
  }, [examId]);

  const fetchExam = async () => {
    try {
      const response = await axios.get(`${API}/admin/exams/${examId}`);
      setFormData({
        title: response.data.title,
        description: response.data.description || "",
        time_limit_minutes: response.data.time_limit_minutes || "",
        questions_per_attempt: response.data.questions_per_attempt || "",
        questions: response.data.questions.length > 0 ? response.data.questions : [emptyQuestion()],
      });
    } catch (error) {
      toast.error("Erro ao carregar simulado");
      navigate("/admin/exams");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.title.trim()) {
      toast.error("Título é obrigatório");
      return;
    }
    
    const validQuestions = formData.questions.filter(q => q.text.trim());
    if (validQuestions.length === 0) {
      toast.error("Adicione pelo menos uma questão");
      return;
    }
    
    // Check if all questions have at least one correct answer
    for (let i = 0; i < validQuestions.length; i++) {
      const q = validQuestions[i];
      const hasCorrect = q.options.some(o => o.is_correct && o.text.trim());
      if (!hasCorrect) {
        toast.error(`Questão ${i + 1} precisa de uma resposta correta`);
        return;
      }
    }
    
    setSaving(true);
    
    try {
      const payload = {
        title: formData.title,
        description: formData.description || null,
        time_limit_minutes: formData.time_limit_minutes ? parseInt(formData.time_limit_minutes) : null,
        questions_per_attempt: formData.questions_per_attempt ? parseInt(formData.questions_per_attempt) : null,
        questions: validQuestions.map(q => ({
          ...q,
          options: q.options.filter(o => o.text.trim()),
        })),
      };
      
      if (isNew) {
        await axios.post(`${API}/admin/exams`, payload);
        toast.success("Simulado criado com sucesso!");
      } else {
        await axios.put(`${API}/admin/exams/${examId}`, payload);
        toast.success("Simulado atualizado!");
      }
      
      navigate("/admin/exams");
    } catch (error) {
      toast.error("Erro ao salvar simulado");
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [...formData.questions, emptyQuestion()],
    });
  };

  const removeQuestion = (index) => {
    setFormData({
      ...formData,
      questions: formData.questions.filter((_, i) => i !== index),
    });
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...formData.questions];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, questions: updated });
  };

  const updateOption = (qIndex, oIndex, field, value) => {
    const updated = [...formData.questions];
    updated[qIndex].options[oIndex] = { 
      ...updated[qIndex].options[oIndex], 
      [field]: value 
    };
    
    // If setting as correct, unset others
    if (field === "is_correct" && value) {
      updated[qIndex].options.forEach((opt, i) => {
        if (i !== oIndex) opt.is_correct = false;
      });
    }
    
    setFormData({ ...formData, questions: updated });
  };

  const addOption = (qIndex) => {
    const updated = [...formData.questions];
    updated[qIndex].options.push({
      id: `opt-${Date.now()}`,
      text: "",
      is_correct: false,
    });
    setFormData({ ...formData, questions: updated });
  };

  const removeOption = (qIndex, oIndex) => {
    const updated = [...formData.questions];
    updated[qIndex].options = updated[qIndex].options.filter((_, i) => i !== oIndex);
    setFormData({ ...formData, questions: updated });
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 md:p-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 loading-skeleton" />
            <div className="h-64 loading-skeleton" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-12 max-w-4xl" data-testid="admin-exam-edit-page">
        {/* Header */}
        <button
          onClick={() => navigate("/admin/exams")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
          data-testid="back-button"
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        <div className="mb-8">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {isNew ? "Novo Simulado" : "Editar Simulado"}
          </p>
          <h1 className="font-serif text-3xl">
            {isNew ? "Criar simulado" : "Editar simulado"}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div className="bento-item mb-6">
            <h3 className="form-section-title">Informações Básicas</h3>
            
            <div className="space-y-4">
              <div>
                <Label className="label-mono">Título *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input-paper"
                  placeholder="Ex: Simulado de Estética Facial"
                  required
                  data-testid="title-input"
                />
              </div>

              <div>
                <Label className="label-mono">Descrição</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição opcional do simulado..."
                  rows={2}
                  data-testid="description-input"
                />
              </div>

              <div>
                <Label className="label-mono">Tempo limite (minutos)</Label>
                <Input
                  type="number"
                  value={formData.time_limit_minutes}
                  onChange={(e) => setFormData({ ...formData, time_limit_minutes: e.target.value })}
                  className="input-paper w-32"
                  placeholder="Ex: 60"
                  min="1"
                  data-testid="time-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Deixe vazio para sem limite de tempo
                </p>
              </div>

              <div>
                <Label className="label-mono">Questões por tentativa</Label>
                <Input
                  type="number"
                  value={formData.questions_per_attempt}
                  onChange={(e) => setFormData({ ...formData, questions_per_attempt: e.target.value })}
                  className="input-paper w-32"
                  placeholder="Ex: 20"
                  min="1"
                  data-testid="questions-per-attempt-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Sorteia X questões aleatórias do banco a cada tentativa. Deixe vazio para usar todas.
                </p>
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl">Questões ({formData.questions.length})</h3>
              <Button
                type="button"
                variant="outline"
                onClick={addQuestion}
                className="btn-secondary"
                data-testid="add-question-button"
              >
                <Plus size={16} className="mr-2" />
                Adicionar Questão
              </Button>
            </div>

            <div className="space-y-6">
              {formData.questions.map((question, qIndex) => (
                <div 
                  key={question.id} 
                  className="bento-item"
                  data-testid={`question-${qIndex}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <GripVertical size={16} className="text-muted-foreground" />
                      <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                        Questão {qIndex + 1}
                      </span>
                    </div>
                    {formData.questions.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeQuestion(qIndex)}
                        data-testid={`remove-question-${qIndex}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="label-mono">Enunciado *</Label>
                      <Textarea
                        value={question.text}
                        onChange={(e) => updateQuestion(qIndex, "text", e.target.value)}
                        placeholder="Digite a pergunta..."
                        rows={2}
                        data-testid={`question-text-${qIndex}`}
                      />
                    </div>

                    <div>
                      <Label className="label-mono">Opções</Label>
                      <div className="space-y-2 mt-2">
                        {question.options.map((option, oIndex) => (
                          <div key={option.id} className="flex items-center gap-2">
                            <Checkbox
                              checked={option.is_correct}
                              onCheckedChange={(checked) => updateOption(qIndex, oIndex, "is_correct", checked)}
                              data-testid={`option-correct-${qIndex}-${oIndex}`}
                            />
                            <Input
                              value={option.text}
                              onChange={(e) => updateOption(qIndex, oIndex, "text", e.target.value)}
                              placeholder={`Opção ${oIndex + 1}`}
                              className="flex-1"
                              data-testid={`option-text-${qIndex}-${oIndex}`}
                            />
                            {question.options.length > 2 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeOption(qIndex, oIndex)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {question.options.length < 6 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => addOption(qIndex)}
                          className="mt-2"
                        >
                          <Plus size={14} className="mr-1" />
                          Adicionar opção
                        </Button>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        Marque a caixa da opção correta
                      </p>
                    </div>

                    <div>
                      <Label className="label-mono">Explicação (opcional)</Label>
                      <Textarea
                        value={question.explanation}
                        onChange={(e) => updateQuestion(qIndex, "explanation", e.target.value)}
                        placeholder="Explicação exibida após responder..."
                        rows={2}
                        data-testid={`question-explanation-${qIndex}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/exams")}
              className="btn-secondary flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1"
              data-testid="save-button"
            >
              {saving ? "Salvando..." : isNew ? "Criar Simulado" : "Salvar Alterações"}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default AdminExamEdit;
